import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap, distinctUntilChanged } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { UtilityService } from '../utility/utility.service';
import { UserRole, MENU_PERMISSIONS } from '../../constants/permissions.constants';
import { API_CONFIG } from '../../config/api.config';

interface ApiPagePermission {
  route: string;
  allowed: boolean;
}

interface ConfigResponse {
  api_version: string;
  page_permissions: ApiPagePermission[];
  feature_flags: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private _authService = inject(AuthService);
  private _utilityService = inject(UtilityService);

  private _apiPermissions: ApiPagePermission[] | null = null;
  private _permissionsReady = new BehaviorSubject<boolean>(false);
  readonly permissionsReady$ = this._permissionsReady.asObservable();

  constructor() {
    this._authService.currentUser.pipe(
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id)
    ).subscribe(user => {
      if (user) {
        this.loadFromApi().subscribe({ error: () => { this._permissionsReady.next(true); } });
      } else {
        this._apiPermissions = null;
        this._permissionsReady.next(false);
      }
    });
  }

  loadFromApi(): Observable<void> {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.modules.config.endpoints.get}`;
    return this._utilityService.request<ConfigResponse>('GET', url, {}, {}, true, { 'x-skip-loader': 'true' }).pipe(
      tap(response => {
        this._apiPermissions = response.page_permissions;
        this._permissionsReady.next(true);
      }),
      map(() => void 0)
    );
  }

  hasAccess(route: string): boolean {
    if (this._apiPermissions) {
      const perm = this._apiPermissions.find(p => p.route === route);
      return perm?.allowed ?? false;
    }
    const user = this._authService.getCurrentUser();
    const userRole = (user?.role || UserRole.USER) as UserRole;
    const permission = MENU_PERMISSIONS.find(p => p.route === route);
    return permission ? permission.allowedRoles.includes(userRole) : true;
  }

  hasRole(role: UserRole): boolean {
    const user = this._authService.getCurrentUser();
    return user?.role === role;
  }
}
